/*
  Heart Magic - shared quiz engine (v1.5)
  ---------------------------------------
  v1.2: product-match quizzes also surface a runner-up recommendation
  (second highest fit) as a secondary card linking to its product page.
  v1.3: startOpen mode (HMQ_SETTINGS.startOpen, from the section's compact
  checkbox) keeps the hero on screen as a slim header and opens the quiz
  on question 1 immediately; "Started" then fires on the first answer.
  Options always render clean (no restored is-picked outline).
  v1.4: resultsFirst mode (HMQ_SETTINGS.resultsFirst) shows results right
  after the last question with no email gate; the gate panel is parented
  into the results screen as a discount-offer email box whose submit swaps
  to a success message instead of revealing results.
  v1.5: on results, product-match quizzes lead with a clickable photo of
  the matched blend ([data-hmq-match-photo]) and the startOpen hero hides;
  SETTINGS.insightLimit caps how many insights render.

  One engine, many quizzes. Each quiz page loads TWO scripts, in order:

    1. its config asset (hm-morning-quiz-config.js, hm-crash-quiz-config.js,
       hm-blend-quiz-config.js, ...) which sets window.HMQ_QUIZ_CONFIG
    2. this engine

  The section (sections/hm-quiz.liquid) provides the markup skeleton and
  sets window.HMQ_SETTINGS (Klaviyo ids). Everything about a specific quiz
  - questions, per-answer dimension points, weights, archetypes, insights,
  first shifts, tracking names, Klaviyo property names - lives in its
  config file. This file should not need editing to change a quiz.

  CONFIG SHAPE (see the config assets for living examples):
    id                'morning'  (storage namespace)
    version           'v1'
    tracking          { prefix: 'MorningQuiz', metric: 'Morning Alignment Quiz Completed' }
    properties        { completed, score, archetype, completedAt, version }  (Klaviyo keys)
    dimensions        [{ key, label, weight, property }]  weights sum to 1
    thresholds        { alignedMin }
    archetypeRules    ordered rules, each one of
                        { allAtLeast: 60, then: 'aligned' }
                        { weakest: { order: [dimKey...],           <- tiebreak order
                            map: { dimKey: 'archetype'
                                   or { ifFlag: 'x', then: 'a', else: 'b' } } } }
                        { strongest: { order: [dimKey...],         <- tiebreak order
                            map: { dimKey: 'archetype' } } }       (product-match quizzes)
    questions         [{ id, text, save?, answers: [{ label, points: {dimKey: n},
                          flag?, goal? }] }]
    archetypes        { key: { name, tagline } }
    insights          [{ id, positive?, when: { anyAnswer: [{ q, a: [idx] }] },
                          title, body }]
    fallbackInsight   shown when nothing matches
    firstShifts       { archetypeKey: { title, body } }
    goalLines         { goalKey: sentence }
    resultProducts    optional { archetypeKey: { url, image, alt, heading?,
                          blurb?, cta?, price_note? } } - swaps the results
                          CTA panel to the matched product

  The results screen tolerates missing pieces: a quiz page may hide the
  score readout, the dimension bars, or the email-series panel (see the
  section's show_score setting) and the engine simply skips them.

  Scoring is deterministic: each dimension normalizes to 0-100 against its
  maximum obtainable points, the overall score is the weighted blend.

  Debug: append ?debug=1 to the page URL.
*/

var HMQEngine = {
  dimKeys: function (cfg) {
    return cfg.dimensions.map(function (d) { return d.key; });
  },

  maxima: function (cfg) {
    var keys = HMQEngine.dimKeys(cfg);
    var max = {};
    keys.forEach(function (k) { max[k] = 0; });
    cfg.questions.forEach(function (q) {
      var best = {};
      keys.forEach(function (k) { best[k] = 0; });
      q.answers.forEach(function (a) {
        keys.forEach(function (k) {
          var v = (a.points && a.points[k]) || 0;
          if (v > best[k]) best[k] = v;
        });
      });
      keys.forEach(function (k) { max[k] += best[k]; });
    });
    return max;
  },

  score: function (cfg, answers) {
    var keys = HMQEngine.dimKeys(cfg);
    var raw = {};
    keys.forEach(function (k) { raw[k] = 0; });
    var flags = {};
    var goal = null;
    cfg.questions.forEach(function (q, i) {
      var a = q.answers[answers[i]];
      if (!a) return;
      keys.forEach(function (k) { raw[k] += (a.points && a.points[k]) || 0; });
      if (a.flag) flags[a.flag] = true;
      if (a.goal) goal = a.goal;
    });
    var max = HMQEngine.maxima(cfg);
    var dims = {};
    keys.forEach(function (k) { dims[k] = max[k] ? Math.round((raw[k] / max[k]) * 100) : 0; });
    var overall = Math.round(cfg.dimensions.reduce(function (sum, d) {
      return sum + dims[d.key] * d.weight;
    }, 0));
    return { raw: raw, max: max, dimensions: dims, overall: overall, flags: flags, goal: goal };
  },

  pickArchetype: function (cfg, result) {
    var d = result.dimensions;
    var keys = HMQEngine.dimKeys(cfg);
    for (var i = 0; i < cfg.archetypeRules.length; i++) {
      var rule = cfg.archetypeRules[i];
      if (typeof rule.allAtLeast === 'number') {
        var all = keys.every(function (k) { return d[k] >= rule.allAtLeast; });
        if (all) return rule.then;
      } else if (rule.weakest) {
        var order = rule.weakest.order;
        var weakest = order.reduce(function (best, k) { return d[k] < d[best] ? k : best; }, order[0]);
        var target = rule.weakest.map[weakest];
        if (typeof target === 'string') return target;
        if (target && target.ifFlag) return result.flags[target.ifFlag] ? target.then : target.else;
      } else if (rule.strongest) {
        var sOrder = rule.strongest.order;
        var strongest = sOrder.reduce(function (best, k) { return d[k] > d[best] ? k : best; }, sOrder[0]);
        var sTarget = rule.strongest.map[strongest];
        if (typeof sTarget === 'string') return sTarget;
      }
    }
    /* Config error fallback: first declared archetype. */
    return Object.keys(cfg.archetypes)[0];
  },

  pickInsights: function (cfg, answers, limit) {
    limit = limit || 3;
    function matches(rule) {
      if (!rule.when || !rule.when.anyAnswer) return false;
      return rule.when.anyAnswer.some(function (c) {
        var qi = -1;
        cfg.questions.forEach(function (q, i) { if (q.id === c.q) qi = i; });
        return qi > -1 && c.a.indexOf(answers[qi]) > -1;
      });
    }
    var picked = cfg.insights.filter(matches);
    if (picked.length === 0 && cfg.fallbackInsight) picked = [cfg.fallbackInsight];
    /* Lead with observations, close with an affirmation when there is one. */
    var neg = picked.filter(function (x) { return !x.positive; });
    var pos = picked.filter(function (x) { return x.positive; });
    return neg.concat(pos).slice(0, limit);
  },

  /*
    Runner-up for product-match (strongest-rule) quizzes: the second
    highest normalized fit, tiebreak following the rule's order. Returns
    null for quizzes without a strongest rule.
  */
  pickRunnerUp: function (cfg, result, archetypeKey) {
    var rule = null;
    for (var i = 0; i < cfg.archetypeRules.length; i++) {
      if (cfg.archetypeRules[i].strongest) { rule = cfg.archetypeRules[i].strongest; break; }
    }
    if (!rule) return null;
    var d = result.dimensions;
    var winnerDim = null;
    rule.order.forEach(function (k) {
      if (winnerDim === null && rule.map[k] === archetypeKey) winnerDim = k;
    });
    var second = null;
    rule.order.forEach(function (k) {
      if (k === winnerDim) return;
      if (second === null || d[k] > d[second]) second = k;
    });
    return second === null ? null : rule.map[second];
  },

  evaluate: function (cfg, answers) {
    var result = HMQEngine.score(cfg, answers);
    var archetypeKey = HMQEngine.pickArchetype(cfg, result);
    var runnerUpKey = HMQEngine.pickRunnerUp(cfg, result, archetypeKey);
    return {
      answers: answers,
      raw: result.raw,
      dimensions: result.dimensions,
      overall: result.overall,
      flags: result.flags,
      goal: result.goal,
      archetypeKey: archetypeKey,
      archetype: cfg.archetypes[archetypeKey],
      runnerUpKey: runnerUpKey,
      runnerUp: runnerUpKey ? cfg.archetypes[runnerUpKey] : null,
      insights: HMQEngine.pickInsights(cfg, answers, 3),
      firstShift: cfg.firstShifts[archetypeKey],
      goalLine: result.goal ? cfg.goalLines[result.goal] : null
    };
  }
};

/* Node test harness support (never true in the browser). */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { HMQEngine: HMQEngine };
}

/* =========================================================================
   UI - browser only
   ========================================================================= */

(function () {
  if (typeof document === 'undefined') return;
  var root = document.querySelector('[data-hm-quiz]');
  var cfg = window.HMQ_QUIZ_CONFIG;
  if (!root || !cfg) return;

  var SETTINGS = window.HMQ_SETTINGS || {};
  var STORE_KEY = 'hm_quiz_' + cfg.id + '_' + cfg.version;
  var ATTR_KEY = 'hm_first_touch';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- analytics: fbq if present, dataLayer always, never throws ---- */
  function track(name, params, opts) {
    try {
      if (typeof window.fbq === 'function') {
        if (opts && opts.standard) window.fbq('track', name, params || {}, opts.eventID ? { eventID: opts.eventID } : undefined);
        else window.fbq('trackCustom', name, params || {});
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params || {}));
    } catch (e) { /* tracking must never break the quiz */ }
  }
  function trackStep(step, params) {
    track(cfg.tracking.prefix + step, Object.assign({ quiz_version: cfg.version, quiz_id: cfg.id }, params || {}));
  }

  /* ---- attribution: first touch wins, never overwritten ---- */
  function attribution() {
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(ATTR_KEY)); } catch (e) { /* storage unavailable */ }
    if (saved && saved.landing_page) return saved;
    var data = { landing_page: location.pathname + location.search, referrer: document.referrer || '' };
    try {
      var params = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = params.get(k);
        if (v) data[k] = v;
      });
    } catch (e) { /* attribution is best effort */ }
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(data)); } catch (e) { /* fine */ }
    return data;
  }
  var attr = attribution();

  /* ---- state, restored from sessionStorage if present ---- */
  var state = { step: -1, answers: [] };
  try {
    var savedState = JSON.parse(sessionStorage.getItem(STORE_KEY));
    if (savedState && Array.isArray(savedState.answers)) state = savedState;
  } catch (e) { /* start fresh */ }
  function persist() {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* fine */ }
  }

  function $(sel) { return root.querySelector(sel); }
  function $all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function setText(sel, text) { var el = $(sel); if (el) el.textContent = text; }
  var screens = {
    landing: $('[data-hmq-screen="landing"]'),
    quiz: $('[data-hmq-screen="quiz"]'),
    reading: $('[data-hmq-screen="reading"]'),
    gate: $('[data-hmq-screen="gate"]'),
    results: $('[data-hmq-screen="results"]')
  };

  /*
    startOpen (the section's "compact" mode): the hero stays on screen as a
    slim header and the quiz opens on question 1 immediately, no Begin click.
    resultsFirst: results show right after the last question with no email
    gate; the email box moves into the results page as a discount offer.
  */
  var startOpen = !!SETTINGS.startOpen;
  var resultsFirst = !!SETTINGS.resultsFirst;

  /* In resultsFirst mode the gate panel lives inside the results screen. */
  if (resultsFirst && screens.gate) {
    var captureSlot = $('[data-hmq-capture-slot]');
    if (captureSlot) { captureSlot.appendChild(screens.gate); screens.gate.hidden = false; }
  }

  var progressFired = {};
  function show(name, opts) {
    Object.keys(screens).forEach(function (k) {
      if (!screens[k]) return;
      /* startOpen keeps the hero up as a header, except on the results
         page, where the matched product photo takes over the top spot. */
      if (k === 'landing' && startOpen) { screens[k].hidden = name === 'results'; return; }
      if (k === 'gate' && resultsFirst) return; /* parented into results */
      screens[k].hidden = k !== name;
    });
    if (name !== 'landing' && !(opts && opts.noScroll)) {
      var anchor = $('[data-hmq-stage]') || root;
      anchor.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  /* ---- quiz rendering ---- */
  var qTitle = $('[data-hmq-question]');
  var qOptions = $('[data-hmq-options]');
  var qDots = $('[data-hmq-dots]');
  var qBack = $('[data-hmq-back]');
  var qStepLabel = $('[data-hmq-steplabel]');

  function renderDots() {
    if (!qDots) return;
    qDots.innerHTML = '';
    cfg.questions.forEach(function (_, i) {
      var dot = document.createElement('span');
      dot.className = 'hmq-dot' + (i < state.step ? ' is-done' : i === state.step ? ' is-current' : '');
      qDots.appendChild(dot);
    });
  }

  function renderQuestion() {
    var q = cfg.questions[state.step];
    if (!q) return;
    qTitle.textContent = q.text;
    qStepLabel.textContent = 'Question ' + (state.step + 1) + ' of ' + cfg.questions.length;
    renderDots();
    qBack.hidden = state.step === 0;
    qOptions.innerHTML = '';
    q.answers.forEach(function (a, i) {
      var b = document.createElement('button');
      b.type = 'button';
      /* Options always render clean; is-picked is only the click flash. */
      b.className = 'hmq-option';
      b.textContent = a.label;
      b.addEventListener('click', function () { choose(i, b); });
      qOptions.appendChild(b);
    });
    /* Move focus to the question for screen readers and keyboard users. */
    qTitle.setAttribute('tabindex', '-1');
    qTitle.focus({ preventScroll: true });
  }

  /* In startOpen mode "Started" means the first answer, not a Begin click. */
  var startedFired = state.step > 0 || state.answers.length > 0;

  function choose(index, btn) {
    if (!startedFired) { startedFired = true; if (startOpen) trackStep('Started'); }
    state.answers[state.step] = index;
    persist();
    $all('.hmq-option').forEach(function (el) { el.classList.remove('is-picked'); });
    btn.classList.add('is-picked');

    var pct = Math.round(((state.step + 1) / cfg.questions.length) * 100);
    [25, 50, 75].forEach(function (t) {
      if (pct >= t && !progressFired[t]) { progressFired[t] = true; trackStep(String(t)); }
    });

    window.setTimeout(function () {
      if (state.step < cfg.questions.length - 1) {
        state.step += 1;
        persist();
        renderQuestion();
      } else {
        finishQuiz();
      }
    }, reducedMotion ? 0 : 260);
  }

  function finishQuiz() {
    state.step = cfg.questions.length; /* marks the quiz finished for session restore */
    persist();
    trackStep('Completed');
    show('reading');
    window.setTimeout(function () {
      if (resultsFirst) revealResults(HMQEngine.evaluate(cfg, state.answers), true);
      else show('gate');
    }, reducedMotion ? 0 : 1500);
  }

  /* ---- Klaviyo submission ---- */
  function klaviyoProps(evaln) {
    var props = {};
    props[cfg.properties.completed] = true;
    props[cfg.properties.score] = evaln.overall;
    props[cfg.properties.archetype] = evaln.archetype.name;
    props[cfg.properties.completedAt] = new Date().toISOString();
    props[cfg.properties.version] = cfg.version;
    cfg.dimensions.forEach(function (d) { if (d.property) props[d.property] = evaln.dimensions[d.key]; });
    cfg.questions.forEach(function (q, i) {
      if (q.save && q.answers[state.answers[i]]) props[q.save] = q.answers[state.answers[i]].label;
    });
    if (evaln.runnerUp) props[cfg.properties.archetype + '_second'] = evaln.runnerUp.name;
    return props;
  }

  function klaviyoPayloads(email, firstName, evaln) {
    var props = klaviyoProps(evaln);
    var eventProps = Object.assign({ quiz_version: cfg.version }, props, attr);
    return {
      subscription: {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: cfg.tracking.metric.replace(' Completed', ''),
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  first_name: firstName || undefined,
                  properties: props
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: SETTINGS.klaviyoListId } } }
        }
      },
      event: {
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: cfg.tracking.metric } } },
            profile: { data: { type: 'profile', attributes: { email: email, first_name: firstName || undefined } } },
            properties: eventProps
          }
        }
      }
    };
  }

  function postKlaviyo(path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + SETTINGS.klaviyoCompanyId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('klaviyo ' + path + ' ' + res.status);
      return res;
    });
  }

  function submitToKlaviyo(email, firstName, evaln) {
    var payloads = klaviyoPayloads(email, firstName, evaln);
    return Promise.all([
      postKlaviyo('subscriptions', payloads.subscription),
      postKlaviyo('events', payloads.event)
    ]);
  }

  /* ---- email gate ---- */
  var form = $('[data-hmq-form]');
  var gateError = $('[data-hmq-gate-error]');
  var gateBtn = $('[data-hmq-gate-submit]');
  var failCount = 0;

  function setGateBusy(busy) {
    if (!gateBtn) return;
    gateBtn.disabled = busy;
    gateBtn.textContent = busy ? 'One moment' : gateBtn.getAttribute('data-label');
  }

  function looksLikeEmail(value) {
    var at = value.indexOf('@');
    var dot = value.lastIndexOf('.');
    return at > 0 && dot > at + 1 && dot < value.length - 1 && value.indexOf(' ') === -1;
  }

  /* resultsFirst success state: swap the form for the discount reveal. */
  function showCaptureSuccess() {
    var success = $('[data-hmq-gate-success]');
    if (form) form.hidden = true;
    var fine = $('.hmq-fineprint');
    if (fine) fine.hidden = true;
    gateError.hidden = true;
    if (success) success.hidden = false;
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = form.email.value.trim();
      var firstName = form.first_name.value.trim();
      if (!looksLikeEmail(email)) {
        gateError.textContent = 'Please enter a valid email address.';
        gateError.hidden = false;
        form.email.focus();
        return;
      }
      gateError.hidden = true;
      setGateBusy(true);
      var evaln = HMQEngine.evaluate(cfg, state.answers);
      submitToKlaviyo(email, firstName, evaln).then(function () {
        var leadId = 'hmq-' + cfg.id + '-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
        track('Lead', { content_name: cfg.tracking.metric.replace(' Completed', ''), quiz_version: cfg.version }, { standard: true, eventID: leadId });
        if (resultsFirst) showCaptureSuccess();
        else revealResults(evaln, true);
      }).catch(function () {
        setGateBusy(false);
        failCount += 1;
        if (failCount >= 2) {
          if (resultsFirst) {
            /* The code works regardless, so hand it over anyway. */
            showCaptureSuccess();
            return;
          }
          /* Do not hold their results hostage to a network problem. */
          gateError.textContent = 'We could not reach our email service, so your reset may not arrive. Here are your results anyway.';
          gateError.hidden = false;
          window.setTimeout(function () { revealResults(evaln, false); }, 1200);
        } else {
          gateError.textContent = 'Something interrupted the connection. Your answers are safe on this page. Please try again.';
          gateError.hidden = false;
        }
      });
    });
  }

  /* ---- results ---- */
  function revealResults(evaln, emailOk) {
    show('results');
    trackStep('ResultsViewed', { archetype: evaln.archetype.name, score: evaln.overall });

    setText('[data-hmq-archetype]', evaln.archetype.name);
    setText('[data-hmq-tagline]', evaln.archetype.tagline);
    if (evaln.goalLine) setText('[data-hmq-goalline]', evaln.goalLine);

    /* Overall score counts up (skipped when the page hides the score) */
    var scoreEl = $('[data-hmq-score]');
    if (scoreEl) {
      var target = evaln.overall;
      if (reducedMotion) { scoreEl.textContent = target; }
      else {
        var n = 0;
        var timer = window.setInterval(function () {
          n += Math.max(1, Math.round(target / 36));
          if (n >= target) { n = target; window.clearInterval(timer); }
          scoreEl.textContent = n;
        }, 26);
      }
    }

    /* Dimension bars (skipped when the page hides them) */
    var barsWrap = $('[data-hmq-bars]');
    if (barsWrap) {
      barsWrap.innerHTML = '';
      cfg.dimensions.forEach(function (d, i) {
        var v = evaln.dimensions[d.key];
        var row = document.createElement('div');
        row.className = 'hmq-bar-row';
        row.innerHTML =
          '<span class="hmq-bar-label">' + d.label + '</span>' +
          '<span class="hmq-bar-track" role="img" aria-label="' + d.label + ' ' + v + ' out of 100"><span class="hmq-bar-fill"></span></span>' +
          '<span class="hmq-bar-value">' + v + '</span>';
        barsWrap.appendChild(row);
        var fill = row.querySelector('.hmq-bar-fill');
        if (reducedMotion) fill.style.width = v + '%';
        else window.setTimeout(function () { fill.style.width = v + '%'; }, 150 + i * 140);
      });
    }

    /* Insights (a page can cap how many show via the section setting) */
    var insightsWrap = $('[data-hmq-insights]');
    if (insightsWrap) {
      insightsWrap.innerHTML = '';
      evaln.insights.slice(0, SETTINGS.insightLimit || 3).forEach(function (ins) {
        var el = document.createElement('div');
        el.className = 'hmq-insight';
        var h = document.createElement('h4');
        h.textContent = ins.title;
        var p = document.createElement('p');
        p.textContent = ins.body;
        el.appendChild(h);
        el.appendChild(p);
        insightsWrap.appendChild(el);
      });
    }

    /* First shift */
    if (evaln.firstShift) {
      setText('[data-hmq-shift-title]', evaln.firstShift.title);
      setText('[data-hmq-shift-body]', evaln.firstShift.body);
    }

    /* Matched product CTA (product-match quizzes only) */
    var rp = cfg.resultProducts && cfg.resultProducts[evaln.archetypeKey];

    /* Hero photo of the match at the very top, clicking through to its page */
    var matchPhoto = $('[data-hmq-match-photo]');
    if (rp && matchPhoto) {
      var mpImg = matchPhoto.querySelector('img');
      if (mpImg && rp.image) { mpImg.src = rp.image; mpImg.alt = rp.alt || evaln.archetype.name; }
      if (rp.url) matchPhoto.setAttribute('href', rp.url);
      matchPhoto.setAttribute('aria-label', 'Shop ' + evaln.archetype.name);
      matchPhoto.hidden = false;
    }

    var ctaWrap = $('.hmq-cta');
    if (rp && ctaWrap) {
      var ctaImg = ctaWrap.querySelector('img');
      if (ctaImg && rp.image) { ctaImg.src = rp.image; ctaImg.alt = rp.alt || evaln.archetype.name; }
      var ctaHeading = ctaWrap.querySelector('h3');
      if (ctaHeading && rp.heading) ctaHeading.textContent = rp.heading;
      var ctaCopy = ctaWrap.querySelector('p:not(.hmq-cta-price)');
      if (ctaCopy && rp.blurb) ctaCopy.textContent = rp.blurb;
      /* Every link in the CTA panel points at the matched product page. */
      if (rp.url) {
        Array.prototype.slice.call(ctaWrap.querySelectorAll('a[data-hmq-cta]')).forEach(function (a) {
          a.setAttribute('href', rp.url);
        });
      }
      var ctaBtn = ctaWrap.querySelector('a.hm-btn[data-hmq-cta]');
      if (ctaBtn && rp.cta) ctaBtn.textContent = rp.cta;
      var ctaPrice = ctaWrap.querySelector('.hmq-cta-price');
      if (ctaPrice && rp.price_note) ctaPrice.textContent = rp.price_note;
    }

    /* Second recommendation: the next best fit, always shown when known */
    var ru = evaln.runnerUpKey && cfg.resultProducts && cfg.resultProducts[evaln.runnerUpKey];
    var secWrap = $('[data-hmq-secondary]');
    if (ru && secWrap && evaln.runnerUpKey !== evaln.archetypeKey) {
      var sImg = secWrap.querySelector('img');
      if (sImg && ru.image) { sImg.src = ru.image; sImg.alt = ru.alt || evaln.runnerUp.name; }
      setText('[data-hmq-secondary-name]', evaln.runnerUp.name);
      setText('[data-hmq-secondary-line]', evaln.runnerUp.tagline);
      if (ru.url) secWrap.setAttribute('href', ru.url);
      secWrap.hidden = false;
    }

    /* Reset note honesty: only promise the email if the submit worked */
    var resetNote = $('[data-hmq-reset-note]');
    if (resetNote && !emailOk) resetNote.textContent = 'We could not confirm your email just now, so your email series may not arrive. Everything above is yours to keep.';

    persist();
    renderDebug(evaln);
  }

  /* ---- product CTA tracking ---- */
  $all('[data-hmq-cta]').forEach(function (a) {
    a.addEventListener('click', function () {
      trackStep('ProductCTA', { destination: a.getAttribute('href') });
    });
  });

  /* ---- debug panel (?debug=1 only) ---- */
  function renderDebug(evaln) {
    var on = /[?&]debug=1/.test(location.search);
    var panel = $('[data-hmq-debug]');
    if (!on || !panel) return;
    panel.hidden = false;
    panel.textContent = JSON.stringify({
      quiz: cfg.id,
      answers: state.answers,
      raw: evaln.raw,
      dimensions: evaln.dimensions,
      overall: evaln.overall,
      flags: evaln.flags,
      goal: evaln.goal,
      archetype: evaln.archetypeKey,
      runnerUp: evaln.runnerUpKey,
      insights: evaln.insights.map(function (i) { return i.id; }),
      attribution: attr
    }, null, 2);
  }

  /* ---- wire up ---- */
  var beginBtn = $('[data-hmq-begin]');
  if (beginBtn) {
    beginBtn.addEventListener('click', function () {
      if (state.step < 0) {
        state.step = 0;
        persist();
        trackStep('Started');
      }
      show('quiz');
      renderQuestion();
    });
  }

  if (qBack) {
    qBack.addEventListener('click', function () {
      if (state.step > 0) { state.step -= 1; persist(); renderQuestion(); }
    });
  }

  /* Restore a session in progress (answers survive accidental reloads). */
  if (state.step >= 0 && state.answers.length > 0 && state.step < cfg.questions.length) {
    show('quiz', { noScroll: startOpen });
    renderQuestion();
  } else if (state.step >= cfg.questions.length) {
    if (resultsFirst) revealResults(HMQEngine.evaluate(cfg, state.answers), true);
    else show('gate', { noScroll: startOpen });
  } else if (startOpen) {
    state.step = 0;
    show('quiz', { noScroll: true });
    renderQuestion();
  } else {
    show('landing');
  }

  trackStep('Viewed');

  /* Expose for the debug console. */
  window.HMQ = { config: cfg, engine: HMQEngine, state: state };
})();